import {
	Button,
	Checkbox,
	FormControlLabel,
	MuiThemeProvider,
	TextField,
	createMuiTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	deleteFoodFamily,
	getFoodFamilyById,
	postFoodFamily,
	updateFoodFamily,
} from "../../../../api/foodFamily";
import { checkIsEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { changes, setChanges } from "../../../../utils/globals";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyFoodFamily() {
	return {
		fullName: {},
		imageURL: null,
		active: true,
	};
}

export default function EditFoodFamiliesPage() {
	const [foodFamily, setFoodFamily] = useState(getEmptyFoodFamily());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(0);
	const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);

	const foodFamilyId = useParams().id;
	const history = useHistory();
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	// const [changes, setChanges] = useState(-2);
	useEffect(() => setChanges(-2), []);
	useEffect(() => {
		if (!foodFamilyId) setChanges(changes+2);
		else setChanges(changes+1);
	}, [foodFamily])

	useEffect(() => {
		if (!foodFamilyId) {
			disableLoadingData();
			return;
		}
		getFoodFamilyById(foodFamilyId)
			.then((res) => {
				if (res.status === 200) {
					setFoodFamily(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get food family.",
				});
				setChanges(0);
				history.push("/food-families");
			});
	}, [foodFamilyId, disableLoadingData, history]);

	function saveFoodFamily() {
		if (checkIsEmpty(foodFamily.fullName))
			return alertError({
				error: null,
				customMessage:
					"The name is required in at least one of the languages.",
			});
		if (!foodFamilyId) {
			postFoodFamily(foodFamily, selectedImage)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Food family successfully created.",
						});
						setChanges(0);
						history.push("/food-families");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save food family.",
					});
				});
		} else {
			updateFoodFamily(foodFamilyId, foodFamily, selectedImage)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						setChanges(0);
						history.push("/food-families");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === " ") return;
		if (lang) {
			if (!foodFamily[element]) foodFamily[element] = {};
			let newText = foodFamily[element];
			newText[lang] = event.target.value;
			setFoodFamily({ ...foodFamily, [element]: newText });
		} else
			setFoodFamily({
				...foodFamily,
				[element]: event.target.value,
			});
	};

	const renderMultilanguageTabContent = (lang) => {
		return (
			<>
				<br />
				<TextField
					id={`fullName`}
					label="Full name"
					value={
						(foodFamily.fullName && foodFamily.fullName[lang]) || ""
					}
					onChange={handleChange("fullName", lang)}
					InputLabelProps={{
						shrink: true,
					}}
					margin="normal"
					variant="standard"
					required
				/>
			</>
		);
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
				<CardHeader title={foodFamily?._id?'Food family: ' + (foodFamily.fullName?foodFamily.fullName.es:''):'New food family'}>
					<div className="mt-5">
						<Button
							onClick={() => {
								if (changes > 0) setOpenConfirmDialog(1);
								else history.push("/food-families");
							}}
							variant="outlined"
							style={{ marginRight: "20px" }}
						>
							Back
						</Button>
						<Button
							onClick={() => saveFoodFamily()}
							variant="outlined"
							color="primary"
							style={{ marginRight: "20px" }}
						>
							Save food family
						</Button>
						<ConfirmDialog
							title={
								"Are you sure you want to go back? You will lose all your changes"
							}
							open={openConfirmDialog === 1}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								setChanges(0);
								history.push("/food-families")
							}}
						/>
						{foodFamilyId && user?.role.includes("admin") && (
							<>
								<MuiThemeProvider theme={theme}>
									<Button
										onClick={() => setOpenConfirmDialog(2)}
										variant="outlined"
										color="secondary"
										style={{ marginRight: "20px" }}
									>
										Delete food family
									</Button>
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											marginLeft: "auto",
										}}
									></div>
								</MuiThemeProvider>

								<ConfirmDialog
									title={
										"Are you sure you want to delete this food family?"
									}
									open={openConfirmDialog === 2}
									setOpen={setOpenConfirmDialog}
									onConfirm={() => {
										deleteFoodFamily(foodFamilyId)
											.then((res) => {
												if (
													res.status === 204 ||
													res.status === 200
												) {
													alertSuccess({
														title: "Deleted!",
														customMessage:
															"Food family deleted successfully",
													});
													setChanges(0);
													history.push("/food-families");
												}
											})
											.catch((error) => {
												alertError({
													error: error,
													customMessage:
														"Could not delete food family.",
												});
											});
									}}
								/>
							</>
						)}
					</div>
				</CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent={
								renderMultilanguageTabContent
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={foodFamily.active}
									onChange={() =>
										setFoodFamily({
											...foodFamily,
											active: !foodFamily.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>
					</CardBody>
				</Card>
			</>
		);
}
